// skills/crafting/craftingUI.js
// Crafting UI - Gatherer-style card layout with category tabs

import { craftingSkill } from './crafting.js';
import { JEWELRY, TOOLS, ARMOR_BY_TIER, ARMOR_TIERS, MAGIC_ARMOR_BY_TIER, MAGIC_ARMOR_TIERS } from './craftingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getItemName, getItemIcon, hasSprite } from '../../data/items/index.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier, isGoldCostWaived } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { buildEquipmentStatsTooltip } from '../../ui/equipmentTooltip.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';

// Track state
let lastLootMultiplier = 1;
let currentCategory = 'jewelry';
let currentJewelryType = 'band';  // 'band', 'pendant', 'all'
let currentArmorTier = 1;
let currentMagicArmorTier = 1;

/**
 * Get the current category (for persisting tab state)
 */
export function getCurrentCategory() {
    return currentCategory;
}

/**
 * Render compact header with tool upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.CRAFTING);
    const xpPerHour = getXPPerHour(SKILLS.CRAFTING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.CRAFTING);

    // Tool info
    const activeTool = craftingSkill.getActiveBuff();
    const tools = Object.values(TOOLS);
    const currentIndex = tools.findIndex(t => t.id === activeTool.id);
    const nextTool = tools[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextTool) {
        const canAfford = playerGold >= nextTool.price;
        if (skill.level < nextTool.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextTool.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.craftingUI.upgradeTool('${nextTool.id}')">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.CRAFTING)}</span>
                    <span class="compact-name">Crafting</span>
                    ${renderSkillInfo(SKILLS.CRAFTING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.CRAFTING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Needle:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${progressPercent}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render main category tabs (Jewelry, Ranged, Magic)
 */
function renderCategoryTabs() {
    const jewelryTabIcon = getItemIcon('diamond_pendant');
    const rangedTabIcon = getItemIcon('green_wyrm_helm');
    const magicTabIcon = getItemIcon('aetherweave_helm'); // "Hood" slot
    return `
        <div class="skill-tabs crafting-category-tabs">
            <button class="skill-tab category-tab ${currentCategory === 'jewelry' ? 'active' : ''}"
                    onclick="window.craftingUI.switchCategory('jewelry')">
                <span class="tab-bar-icon silhouette">${jewelryTabIcon}</span>Jewelry
            </button>
            <button class="skill-tab category-tab ${currentCategory === 'ranged' ? 'active' : ''}"
                    onclick="window.craftingUI.switchCategory('ranged')">
                <span class="tab-bar-icon silhouette">${rangedTabIcon}</span>Ranged Armor
            </button>
            <button class="skill-tab category-tab ${currentCategory === 'magic' ? 'active' : ''}"
                    onclick="window.craftingUI.switchCategory('magic')">
                <span class="tab-bar-icon silhouette">${magicTabIcon}</span>Magic Armor
            </button>
        </div>
    `;
}

/**
 * Render tier sub-tabs for armor categories
 */
function renderArmorTierTabs() {
    const skill = gameState.getSkill(SKILLS.CRAFTING);

    const tierTabs = Object.entries(ARMOR_TIERS).map(([tier, info]) => {
        const tierNum = parseInt(tier);
        const isLocked = skill.level < info.level;
        const isActive = currentArmorTier === tierNum;
        const lockIcon = isLocked ? '🔒 ' : '';

        return `
            <button class="skill-tab tier-tab ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}"
                    onclick="window.craftingUI.switchArmorTier(${tierNum})"
                    style="--tab-color: ${info.color}"
                    ${isLocked ? 'title="Requires level ' + info.level + '"' : ''}>
                ${lockIcon}${info.name}
            </button>
        `;
    }).join('');

    return `
        <div class="skill-tabs crafting-tier-tabs">
            ${tierTabs}
        </div>
    `;
}

/**
 * Render tier sub-tabs for magic armor
 */
function renderMagicArmorTierTabs() {
    const skill = gameState.getSkill(SKILLS.CRAFTING);

    const tierTabs = Object.entries(MAGIC_ARMOR_TIERS).map(([tier, info]) => {
        const tierNum = parseInt(tier);
        const isLocked = skill.level < info.level;
        const isActive = currentMagicArmorTier === tierNum;
        const lockIcon = isLocked ? '🔒 ' : '';

        return `
            <button class="skill-tab tier-tab ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}"
                    onclick="window.craftingUI.switchMagicArmorTier(${tierNum})"
                    style="--tab-color: ${info.color}"
                    ${isLocked ? 'title="Requires level ' + info.level + '"' : ''}>
                ${lockIcon}${info.name}
            </button>
        `;
    }).join('');

    return `
        <div class="skill-tabs crafting-tier-tabs">
            ${tierTabs}
        </div>
    `;
}

/**
 * Render type sub-tabs for jewelry (Bands, Pendants)
 */
function renderJewelryTypeTabs() {
    const bandSil = `<span class="tab-bar-icon silhouette">${getItemIcon('diamond_band')}</span>`;
    const pendantSil = `<span class="tab-bar-icon silhouette">${getItemIcon('diamond_pendant')}</span>`;
    const types = [
        { id: 'band', name: 'Bands', icon: bandSil },
        { id: 'pendant', name: 'Pendants', icon: pendantSil },
        { id: 'all', name: 'All', icon: `${bandSil}<span class="tab-icon-sep">|</span>${pendantSil}` }
    ];

    const typeTabs = types.map(type => {
        const isActive = currentJewelryType === type.id;
        return `
            <button class="skill-tab type-tab ${isActive ? 'active' : ''}"
                    onclick="window.craftingUI.switchJewelryType('${type.id}')">
                ${type.icon}${type.name}
            </button>
        `;
    }).join('');

    return `
        <div class="skill-tabs crafting-type-tabs">
            ${typeTabs}
        </div>
    `;
}

/**
 * Render a single jewelry card
 */
function renderJewelryCard(item, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= item.level;
    const isActive = currentAction?.type === 'crafting' && currentAction?.item?.id === item.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = craftingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.CRAFTING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.CRAFTING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = item.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.CRAFTING);
    let effectiveXP = item.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.CRAFTING);
    const talentLootBonus = getTalentLootBonus(SKILLS.CRAFTING);

    // Check all materials
    let hasMaterials = true;
    const materialsHtml = item.inputItems.map(input => {
        const playerCount = gameState.getItemCount(input.itemId);
        const hasEnough = playerCount >= input.quantity;
        if (!hasEnough) hasMaterials = false;
        const inputIcon = getItemIcon(input.itemId);
        const inputName = getItemName(input.itemId);
        return `
            <div class="material-row ${!hasEnough ? 'missing' : ''}">
                <span class="material-icon">${inputIcon}</span>
                <span class="material-name">${inputName}</span>
                <span class="material-count">${playerCount}/${input.quantity}</span>
            </div>
        `;
    }).join('');

    // Output display
    const drop = item.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    // Category icon
    const categoryIcons = { band: '💍', pendant: '📿' };
    const catIcon = categoryIcons[item.category] || '💍';

    const cardClasses = [
        'craft-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-item-id="${item.id}"
             style="--craft-color: ${item.color}">

            <div class="craft-card-header">
                <span class="craft-card-level">${isUnlocked ? `Lvl ${item.level}` : `Req: ${item.level}`}</span>
                <span class="craft-card-icon">${hasSprite(drop.itemId) ? outputIcon : catIcon}</span>
                <span class="craft-card-name">${item.name}</span>
            </div>

            <div class="craft-card-info">
                <div class="craft-stat">
                    <span class="stat-icon crafting-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="craft-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="craft-materials">
                <div class="materials-label">Input</div>
                ${materialsHtml}
            </div>

            <div class="craft-output">
                <div class="output-label">Output</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(item.id)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="craft-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.craftingUI.toggleJewelry('${item.id}')">
                        ${isActive ? 'Stop' : 'Craft'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="craft-progress">
                <div class="craft-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a single armor card
 */
function renderArmorCard(item, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= item.level;
    const isActive = currentAction?.type === 'crafting' && currentAction?.item?.id === item.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs (includes Master Tailor bonus for armor)
    let speedMultiplier = craftingSkill.getEffectiveSpeedMultiplier();
    const armorSpeedBonus = craftingSkill.getArmorCraftSpeedBonus();
    if (armorSpeedBonus > 0) speedMultiplier *= (1 + armorSpeedBonus);
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.CRAFTING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.CRAFTING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = item.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.CRAFTING);
    let effectiveXP = item.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.CRAFTING);
    const talentLootBonus = getTalentLootBonus(SKILLS.CRAFTING);

    // Check gold cost (corruption may waive it)
    const goldWaived = isGoldCostWaived(SKILLS.CRAFTING);
    const playerGold = gameState.getGold();
    const hasGold = goldWaived || playerGold >= item.goldCost;

    // Check all materials
    let hasMaterials = true;
    const materialsHtml = item.inputItems.map(input => {
        const playerCount = gameState.getItemCount(input.itemId);
        const hasEnough = playerCount >= input.quantity;
        if (!hasEnough) hasMaterials = false;
        const inputIcon = getItemIcon(input.itemId);
        const inputName = getItemName(input.itemId);
        return `
            <div class="material-row ${!hasEnough ? 'missing' : ''}">
                <span class="material-icon">${inputIcon}</span>
                <span class="material-name">${inputName}</span>
                <span class="material-count">${playerCount}/${input.quantity}</span>
            </div>
        `;
    }).join('');

    // Gold cost display - use material-row format for consistency
    const goldClass = hasGold ? '' : 'missing';
    const goldWaivedText = goldWaived ? ' <span class="waived-text">(waived)</span>' : '';
    const goldCountDisplay = goldWaived
        ? `<span class="material-count">${formatWithCommas(item.goldCost)}${goldWaivedText}</span>`
        : `<span class="material-count">${formatWithCommas(playerGold)}/${formatWithCommas(item.goldCost)}</span>`;

    // Output display
    const drop = item.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    // Slot icon
    const slotIcons = {
        helm: '🪖', shoulders: '🛡️', gloves: '🧤',
        chestplate: '👕', legguards: '👖', boots: '👢'
    };
    const slotIcon = slotIcons[item.slot] || '🛡️';

    const cardClasses = [
        'craft-card armor-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        (!hasMaterials || !hasGold) && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-item-id="${item.id}"
             data-tier="${item.tier}"
             style="--craft-color: ${item.color}">

            <div class="craft-card-header">
                <span class="craft-card-level">${isUnlocked ? `Lvl ${item.level}` : `Req: ${item.level}`}</span>
                <span class="craft-card-icon">${hasSprite(drop.itemId) ? outputIcon : slotIcon}</span>
                <span class="craft-card-name">${item.name}</span>
            </div>

            <div class="craft-card-info">
                <div class="craft-stat">
                    <span class="stat-icon crafting-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="craft-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="craft-materials">
                <div class="materials-label">Input</div>
                ${materialsHtml}
                <div class="material-row gold-row ${goldClass}">
                    <span class="material-icon">${getItemIcon('coins')}</span>
                    <span class="material-name">Gold</span>
                    ${goldCountDisplay}
                </div>
            </div>

            <div class="craft-output">
                <div class="output-label">Output</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(item.id)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="craft-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.craftingUI.toggleArmor('${item.id}', ${item.tier})">
                        ${isActive ? 'Stop' : 'Craft'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="craft-progress">
                <div class="craft-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a single magic armor card
 */
function renderMagicArmorCard(item, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= item.level;
    const isActive = currentAction?.type === 'crafting' && currentAction?.item?.id === item.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs (includes Master Tailor bonus for armor)
    let speedMultiplier = craftingSkill.getEffectiveSpeedMultiplier();
    const armorSpeedBonus = craftingSkill.getArmorCraftSpeedBonus();
    if (armorSpeedBonus > 0) speedMultiplier *= (1 + armorSpeedBonus);
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.CRAFTING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.CRAFTING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = item.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.CRAFTING);
    let effectiveXP = item.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.CRAFTING);
    const talentLootBonus = getTalentLootBonus(SKILLS.CRAFTING);

    // Check gold cost (corruption may waive it)
    const goldWaived = isGoldCostWaived(SKILLS.CRAFTING);
    const playerGold = gameState.getGold();
    const hasGold = goldWaived || playerGold >= item.goldCost;

    // Check all materials
    let hasMaterials = true;
    const materialsHtml = item.inputItems.map(input => {
        const playerCount = gameState.getItemCount(input.itemId);
        const hasEnough = playerCount >= input.quantity;
        if (!hasEnough) hasMaterials = false;
        const inputIcon = getItemIcon(input.itemId);
        const inputName = getItemName(input.itemId);
        return `
            <div class="material-row ${!hasEnough ? 'missing' : ''}">
                <span class="material-icon">${inputIcon}</span>
                <span class="material-name">${inputName}</span>
                <span class="material-count">${playerCount}/${input.quantity}</span>
            </div>
        `;
    }).join('');

    // Gold cost display - use material-row format for consistency
    const goldClass = hasGold ? '' : 'missing';
    const goldWaivedText = goldWaived ? ' <span class="waived-text">(waived)</span>' : '';
    const goldCountDisplay = goldWaived
        ? `<span class="material-count">${formatWithCommas(item.goldCost)}${goldWaivedText}</span>`
        : `<span class="material-count">${formatWithCommas(playerGold)}/${formatWithCommas(item.goldCost)}</span>`;

    // Output display
    const drop = item.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    // Slot icon (magic armor uses different names)
    const slotIcons = {
        helm: '🧙', shoulders: '✨', gloves: '🌟',
        chestplate: '🌌', legguards: '💫', boots: '⭐'
    };
    const slotIcon = slotIcons[item.slot] || '🔮';

    const cardClasses = [
        'craft-card armor-card magic-armor-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        (!hasMaterials || !hasGold) && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-item-id="${item.id}"
             data-tier="${item.tier}"
             style="--craft-color: ${item.color}">

            <div class="craft-card-header">
                <span class="craft-card-level">${isUnlocked ? `Lvl ${item.level}` : `Req: ${item.level}`}</span>
                <span class="craft-card-icon">${hasSprite(drop.itemId) ? outputIcon : slotIcon}</span>
                <span class="craft-card-name">${item.name}</span>
            </div>

            <div class="craft-card-info">
                <div class="craft-stat">
                    <span class="stat-icon crafting-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="craft-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="craft-materials">
                <div class="materials-label">Input</div>
                ${materialsHtml}
                <div class="material-row gold-row ${goldClass}">
                    <span class="material-icon">${getItemIcon('coins')}</span>
                    <span class="material-name">Gold</span>
                    ${goldCountDisplay}
                </div>
            </div>

            <div class="craft-output">
                <div class="output-label">Output</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(item.id)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="craft-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.craftingUI.toggleMagicArmor('${item.id}', ${item.tier})">
                        ${isActive ? 'Stop' : 'Craft'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="craft-progress">
                <div class="craft-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render jewelry grid by category
 */
function renderJewelrySection(title, icon, items, skill, currentAction) {
    const cardsHtml = items.map(item => renderJewelryCard(item, skill.level, currentAction)).join('');

    return `
        <div class="craft-section">
            <h3 class="craft-section-title">${icon} ${title}</h3>
            <div class="craft-grid">
                ${cardsHtml}
            </div>
        </div>
    `;
}

export function switchCategory(category) {
    currentCategory = category;
    if (category === 'jewelry') {
        renderJewelryUI();
    } else if (category === 'ranged') {
        renderArmorUI(currentArmorTier);
    } else if (category === 'magic') {
        renderMagicArmorUI(currentMagicArmorTier);
    }
}

export function switchMagicArmorTier(tier) {
    currentMagicArmorTier = tier;
    renderMagicArmorUI(tier);
}

export function switchArmorTier(tier) {
    currentArmorTier = tier;
    renderArmorUI(tier);
}

export function switchJewelryType(type) {
    currentJewelryType = type;
    renderJewelryUI();
}

// Legacy switchTab for compatibility
export function switchTab(tab) {
    if (tab === 'jewelry') {
        switchCategory('jewelry');
    } else if (tab.startsWith('armor_')) {
        const tier = parseInt(tab.split('_')[1]);
        currentCategory = 'ranged';
        switchArmorTier(tier);
    }
}

export function renderCraftingUI(params = {}) {
    if (currentCategory === 'ranged') {
        renderArmorUI(currentArmorTier);
    } else {
        renderJewelryUI();
    }
}

/**
 * Render the jewelry interface
 */
export function renderJewelryUI() {
    currentCategory = 'jewelry';

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.CRAFTING);
    const currentAction = gameState.getCurrentAction();

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.CRAFTING);

    // Filter jewelry by selected type
    let jewelryItems;
    if (currentJewelryType === 'all') {
        jewelryItems = Object.values(JEWELRY).sort((a, b) => a.level - b.level);
    } else {
        jewelryItems = Object.values(JEWELRY)
            .filter(j => j.category === currentJewelryType)
            .sort((a, b) => a.level - b.level);
    }

    const cardsHtml = jewelryItems.map(item =>
        renderJewelryCard(item, skill.level, currentAction)
    ).join('');

    container.innerHTML = `
        <div class="crafting-interface">
            ${renderCompactHeader()}
            ${renderCategoryTabs()}
            ${renderJewelryTypeTabs()}
            <div class="craft-content jewelry-content">
                <div class="craft-grid jewelry-grid">
                    ${cardsHtml}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render the armor interface
 */
export function renderArmorUI(tier = currentArmorTier) {
    currentCategory = 'ranged';
    currentArmorTier = tier;

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.CRAFTING);
    const currentAction = gameState.getCurrentAction();
    const tierInfo = ARMOR_TIERS[tier];

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.CRAFTING);

    // Get armor recipes for this tier, sorted by slot
    const slotOrder = ['gloves', 'boots', 'helm', 'shoulders', 'legguards', 'chestplate'];
    const armorRecipes = Object.values(ARMOR_BY_TIER[tier] || {})
        .sort((a, b) => slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot));

    const armorCardsHtml = armorRecipes
        .map(item => renderArmorCard(item, skill.level, currentAction))
        .join('');

    container.innerHTML = `
        <div class="crafting-interface">
            ${renderCompactHeader()}
            ${renderCategoryTabs()}
            ${renderArmorTierTabs()}
            <div class="craft-content armor-content">
                <div class="craft-grid armor-grid">
                    ${armorCardsHtml}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render the magic armor interface
 */
export function renderMagicArmorUI(tier = currentMagicArmorTier) {
    currentCategory = 'magic';
    currentMagicArmorTier = tier;

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.CRAFTING);
    const currentAction = gameState.getCurrentAction();
    const tierInfo = MAGIC_ARMOR_TIERS[tier];

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.CRAFTING);

    // Get magic armor recipes for this tier, sorted by slot
    const slotOrder = ['gloves', 'boots', 'helm', 'shoulders', 'legguards', 'chestplate'];
    const magicArmorRecipes = Object.values(MAGIC_ARMOR_BY_TIER[tier] || {})
        .sort((a, b) => slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot));

    const magicArmorCardsHtml = magicArmorRecipes
        .map(item => renderMagicArmorCard(item, skill.level, currentAction))
        .join('');

    container.innerHTML = `
        <div class="crafting-interface">
            ${renderCompactHeader()}
            ${renderCategoryTabs()}
            ${renderMagicArmorTierTabs()}
            <div class="craft-content magic-armor-content">
                <div class="craft-grid armor-grid magic-grid">
                    ${magicArmorCardsHtml}
                </div>
            </div>
        </div>
    `;
}

export function toggleMagicArmor(itemId, tier) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.item?.id === itemId) {
        craftingSkill.stopCrafting();
    } else {
        const result = craftingSkill.canCraftMagicArmor(itemId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            craftingSkill.startCraftingMagicArmor(itemId);
        }
    }

    renderMagicArmorUI(tier);
}

export function toggleJewelry(itemId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.item?.id === itemId) {
        craftingSkill.stopCrafting();
    } else {
        const result = craftingSkill.canCraftJewelry(itemId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            craftingSkill.startCraftingJewelry(itemId);
        }
    }

    renderJewelryUI();
}

export function toggleArmor(itemId, tier) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.item?.id === itemId) {
        craftingSkill.stopCrafting();
    } else {
        const result = craftingSkill.canCraftArmor(itemId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            craftingSkill.startCraftingArmor(itemId);
        }
    }

    renderArmorUI(tier);
}

export function upgradeTool(toolId) {
    const result = craftingSkill.upgradeTool(toolId);

    if (result.success) {
        if (currentCategory === 'jewelry') {
            renderJewelryUI();
        } else {
            renderArmorUI(currentArmorTier);
        }
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update crafting UI each tick
 */
export function updateCraftingUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.CRAFTING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            if (currentCategory === 'jewelry') {
                renderJewelryUI();
            } else if (currentCategory === 'ranged') {
                renderArmorUI(currentArmorTier);
            } else if (currentCategory === 'magic') {
                renderMagicArmorUI(currentMagicArmorTier);
            }
        }
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.CRAFTING);
    const xpPerHour = getXPPerHour(SKILLS.CRAFTING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.CRAFTING);

    const xpTotalEl = document.querySelector('.crafting-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.crafting-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.crafting-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.crafting-interface', SKILLS.CRAFTING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.crafting-interface');

    // Update material counts on all visible cards
    updateMaterialCounts();

    // Check for stale progress bars (action stopped)
    const action = gameState.getCurrentAction();
    const isCraftingActive = action && action.type === 'crafting';

    const existingCards = document.querySelectorAll('.crafting-interface .craft-card.active');
    if (existingCards.length > 0 && !isCraftingActive) {
        if (currentCategory === 'jewelry') {
            renderJewelryUI();
        } else if (currentCategory === 'ranged') {
            renderArmorUI(currentArmorTier);
        } else if (currentCategory === 'magic') {
            renderMagicArmorUI(currentMagicArmorTier);
        }
        return;
    }

    // Update action progress if crafting is active
    if (!isCraftingActive) return;

    const activeCard = document.querySelector(`.craft-card[data-item-id="${action.item.id}"]`);
    if (activeCard) {
        const cardProgressFill = activeCard.querySelector('.craft-progress-fill');
        if (cardProgressFill) {
            cardProgressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Update material counts on all visible cards
 */
function updateMaterialCounts() {
    const cards = document.querySelectorAll('.crafting-interface .craft-card');

    cards.forEach(card => {
        const itemId = card.dataset.itemId;
        if (!itemId) return;

        // Find the item (could be jewelry, ranged armor, or magic armor)
        let item = Object.values(JEWELRY).find(j => j.id === itemId);
        if (!item) {
            // Try ranged armor
            for (const tier of Object.keys(ARMOR_BY_TIER)) {
                item = Object.values(ARMOR_BY_TIER[tier]).find(a => a.id === itemId);
                if (item) break;
            }
        }
        if (!item) {
            // Try magic armor
            for (const tier of Object.keys(MAGIC_ARMOR_BY_TIER)) {
                item = Object.values(MAGIC_ARMOR_BY_TIER[tier]).find(a => a.id === itemId);
                if (item) break;
            }
        }
        if (!item) return;

        // Update each material row
        const materialRows = card.querySelectorAll('.material-row');
        let allHaveMaterials = true;

        item.inputItems.forEach((input, index) => {
            const row = materialRows[index];
            if (!row) return;

            const playerCount = gameState.getItemCount(input.itemId);
            const hasEnough = playerCount >= input.quantity;
            if (!hasEnough) allHaveMaterials = false;

            const countEl = row.querySelector('.material-count');
            if (countEl) countEl.textContent = `${playerCount}/${input.quantity}`;

            row.classList.toggle('missing', !hasEnough);
        });

        // Update gold row for armor
        if (item.goldCost) {
            const goldRow = card.querySelector('.gold-row');
            if (goldRow) {
                const goldWaived = isGoldCostWaived(SKILLS.CRAFTING);
                const playerGold = gameState.getGold();
                const hasGold = goldWaived || playerGold >= item.goldCost;
                if (!hasGold) allHaveMaterials = false;
                goldRow.classList.toggle('missing', !hasGold);

                // Update gold count display
                const goldCountEl = goldRow.querySelector('.material-count');
                if (goldCountEl) {
                    if (goldWaived) {
                        goldCountEl.innerHTML = `${formatWithCommas(item.goldCost)} <span class="waived-text">(waived)</span>`;
                    } else {
                        goldCountEl.textContent = `${formatWithCommas(playerGold)}/${formatWithCommas(item.goldCost)}`;
                    }
                }
            }
        }

        card.classList.toggle('no-materials', !allHaveMaterials && !card.classList.contains('locked'));
    });
}

/**
 * Setup crafting skill listeners
 */
export function initCraftingUI() {
    craftingSkill.initializeStartingTool();
    craftingSkill.onCraftingComplete = (item, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Crafting level ${xpResult.newLevel}!`);
        }

        // Only re-render if crafting page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.CRAFTING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                if (currentCategory === 'jewelry') {
                    renderJewelryUI();
                } else if (currentCategory === 'ranged') {
                    renderArmorUI(currentArmorTier);
                } else if (currentCategory === 'magic') {
                    renderMagicArmorUI(currentMagicArmorTier);
                }
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.craftingUI = {
        toggleJewelry,
        toggleArmor,
        toggleMagicArmor,
        upgradeTool,
        switchCategory,
        switchArmorTier,
        switchMagicArmorTier,
        switchJewelryType,
        switchTab
    };
}

export default {
    renderCraftingUI,
    renderJewelryUI,
    renderArmorUI,
    renderMagicArmorUI,
    toggleJewelry,
    toggleArmor,
    toggleMagicArmor,
    upgradeTool,
    updateCraftingUI,
    initCraftingUI,
    switchCategory,
    switchArmorTier,
    switchMagicArmorTier,
    switchJewelryType,
    switchTab,
    getCurrentCategory
};
